/**
 * Typed API client for the Shadow Court backend.
 *
 * Cross-Site Cookie-Auth:
 *   • Worker setzt Session-JWT als `SameSite=None; Secure; HttpOnly` Cookie.
 *   • Damit der Browser den Cookie bei Cross-Origin-Requests zurück schickt,
 *     MUSS jeder fetch `credentials: "include"` setzen. Wrapper `request()`
 *     unten macht das einheitlich.
 *   • Der Worker hat CORS mit `credentials: true` und explizitem Origin
 *     (`CORS_ORIGIN` = Pages-Domain) konfiguriert.
 *
 * Dev-Bypass für Magic-Link (solange kein Email-Provider eingerichtet ist):
 *   • Ist in localStorage unter `sc.authBypass` ein Secret hinterlegt, wird es
 *     bei `/api/auth/request` als `X-Auth-Dev-Bypass`-Header mitgeschickt.
 *     Die Response enthält dann `verifyUrl` direkt — kein Email-Versand.
 *   • Ohne Eintrag verhält sich der Client wie im Normalbetrieb.
 *   • Das Secret wird NIEMALS in URL, Body, Console-Log oder in einen anderen
 *     Endpoint gesteckt. Nur der Request-Endpoint braucht es.
 */

const API_BASE = (import.meta.env.VITE_API_BASE ?? "").replace(/\/$/, "");

export interface ApiErrorIssue {
  path: string;
  code: string;
  message: string;
}

export class ApiError extends Error {
  status: number;
  code?: string;
  issues?: ApiErrorIssue[];
  constructor(status: number, body: unknown, fallback: string) {
    const asObj = (typeof body === "object" && body !== null ? (body as Record<string, unknown>) : {}) as {
      error?: string;
      message?: string;
      issues?: ApiErrorIssue[];
    };
    super(asObj.message ?? fallback);
    this.status = status;
    this.code = asObj.error;
    this.issues = asObj.issues;
  }
}

// --- Low-level fetch wrapper ------------------------------------------------

type HeadersDict = Record<string, string>;

interface InternalRequestInit extends Omit<RequestInit, "headers"> {
  headers?: HeadersDict;
}

async function request<T>(path: string, init: InternalRequestInit = {}): Promise<T> {
  const headers: HeadersDict = {
    "Content-Type": "application/json",
    ...(init.headers ?? {}),
  };
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers,
    credentials: "include",
  });
  if (!res.ok) {
    let body: unknown;
    const text = await res.text();
    try {
      body = JSON.parse(text);
    } catch {
      body = text;
    }
    throw new ApiError(res.status, body, `API ${res.status} ${res.statusText}`);
  }
  return (await res.json()) as T;
}

// --- Dev-Bypass helpers -----------------------------------------------------

const BYPASS_KEY = "sc.authBypass";

/** Liest das Bypass-Secret aus localStorage — nur clientseitig vorhanden. */
export function getAuthBypass(): string | null {
  try {
    const v = localStorage.getItem(BYPASS_KEY);
    return v && v.length > 0 ? v : null;
  } catch {
    return null;
  }
}

/** Schreibt (oder entfernt) das Bypass-Secret. */
export function setAuthBypass(secret: string | null): void {
  try {
    if (secret && secret.length > 0) localStorage.setItem(BYPASS_KEY, secret);
    else localStorage.removeItem(BYPASS_KEY);
  } catch {
    /* private-mode / disabled localStorage — fail silent */
  }
}

// --- Auth -------------------------------------------------------------------

export interface AuthUser {
  id: string;
  email: string;
  display_name: string;
  role: "user" | "affiliate" | "admin";
  ref_code: string;
  referrer_user_id: string | null;
  created_at: string;
}

export interface MagicLinkRequestBody {
  email: string;
  displayName?: string;
  refCode?: string;
}

export interface MagicLinkResponse {
  ok: true;
  /** Nur gesetzt, wenn Dev-Bypass aktiv war — enthält Verify-URL direkt. */
  devBypass?: boolean;
  verifyUrl?: string;
  message?: string;
  expiresInSeconds: number;
  note?: string;
}

export async function requestMagicLink(body: MagicLinkRequestBody): Promise<MagicLinkResponse> {
  const bypass = getAuthBypass();
  const headers: HeadersDict = {};
  if (bypass) headers["X-Auth-Dev-Bypass"] = bypass;
  return request<MagicLinkResponse>("/api/auth/request", {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  });
}

/**
 * Ruft /api/auth/verify mit ?json=1 auf, damit der Browser KEINEN 302-Redirect
 * verfolgt, sondern die JSON-Antwort parsen kann. Der Cookie wird gesetzt,
 * auch wenn wir auf JSON umschalten.
 */
export async function verifyMagicLink(token: string, email: string): Promise<{ ok: true; user: AuthUser }> {
  const u = new URL("/api/auth/verify", API_BASE || window.location.origin);
  u.searchParams.set("token", token);
  u.searchParams.set("email", email);
  u.searchParams.set("json", "1");
  return request<{ ok: true; user: AuthUser }>(u.pathname + u.search);
}

export async function me(): Promise<{ user: AuthUser }> {
  return request<{ user: AuthUser }>("/api/auth/me");
}

export async function logout(): Promise<{ ok: true }> {
  return request<{ ok: true }>("/api/auth/logout", { method: "POST" });
}

// --- Case Upload / Trial ----------------------------------------------------

export type Jurisdiction = "DE" | "DACH" | "EU" | "US";

export interface TrialResult {
  caseId: string;
  /** DE/DACH/EU/US — unter der der Swarm zusammengestellt wurde. */
  jurisdiction?: Jurisdiction;
  winProbability: number;
  estimatedDamages: string;
  estimatedDamagesNumeric: number;
  precedentsUsed: Array<{
    /** Stable Vectorize-ID für Volltext-Nachladung via /api/precedents/:id/full. */
    vectorId: string;
    citation: string;
    relevance: number;
    snippet: string;
    /** Sprint I/3: Felder für klickbare Precedent-Modal. */
    court?: string;
    az?: string;
    date?: string;
    name?: string;
    topics?: string[];
    norms?: string[];
    sourceUrl?: string;
    /** "open_legal_data" → Volltext nachladbar; "landmark" → snippet ist alles. */
    source?: string;
  }>;
  battlePlan: string;
  swarmVerdicts: Array<{
    /** Deterministische Juror-ID (z.B. "de_judge_keller"). */
    jurorId?: string;
    /** Voller Name der fiktiven Persona, z.B. "Dr. Martina Keller". */
    name?: string;
    /** Funktionstitel, z.B. "Richterin am Amtsgericht". */
    title?: string;
    /** Fiktive Kanzlei/Gericht — UI labelt mit "(fiktiv)". */
    affiliation?: string;
    role?: string;
    persona?: string;
    model: string;
    verdict: "favorable" | "neutral" | "unfavorable";
    confidence: number;
    reasoning: string;
  }>;
  settlementRecommendation: number;
  /**
   * Plain-language Empfehlung des "Generischen Anwalts". Optional, weil der
   * Server bei LLM-Ausfällen auch ohne Advocate-Response zurückkehrt.
   * Shape 1:1 synchron zu api/src/lib/advocate.ts (AdvocateOpinion).
   */
  advocateOpinion?: AdvocateOpinion;
  createdAt: string;
}

/**
 * Sprint I (2026-04-29): Optionaler Brief-Vorschlag, den der Advocate-Agent
 * mit ausgibt. Frontend rendert daraus einen Inline-CTA "Brief jetzt entwerfen"
 * direkt unter den Next-Steps und routet auf /app/letter mit Pre-Fill.
 *
 * 1:1 mit api/src/lib/advocate.ts (SuggestedLetter / SuggestedLetterType).
 */
export type SuggestedLetterType =
  | "widerspruch"
  | "mahnung"
  | "mietminderung"
  | "eg261_forderung"
  | "gewaehrleistung";

export interface SuggestedLetter {
  type: SuggestedLetterType;
  recipient?: string;
  headline: string;
  reason: string;
}

export interface AdvocateOpinion {
  lawyerName: string;
  lawyerTitle: string;
  lawyerAffiliation: string;
  headline: string;
  recommendation: "pursue" | "settle" | "drop";
  reasoning: string;
  nextSteps: string[];
  lohntAnwalt: boolean;
  expectedValueEur: number;
  uncertainty: "low" | "medium" | "high";
  /**
   * Sprint I: wenn vorhanden, zeigt B2CAdvisor einen Inline-Brief-CTA
   * mit Pre-Fill-Routing auf /app/letter.
   */
  suggestedLetter?: SuggestedLetter;
}

export async function uploadCase(
  file: File,
  meta: { title: string; caseType: string }
): Promise<{ caseId: string; r2Key: string; size: number }> {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("title", meta.title);
  fd.append("caseType", meta.caseType);

  const res = await fetch(`${API_BASE}/api/upload`, {
    method: "POST",
    body: fd,
    credentials: "include",
  });
  if (!res.ok) {
    let body: unknown;
    const text = await res.text();
    try { body = JSON.parse(text); } catch { body = text; }
    throw new ApiError(res.status, body, `upload failed: ${res.status}`);
  }
  return res.json();
}

/**
 * Erstellt einen text-only case (ohne PDF). Für die Schnellfrage und für
 * den "Beschreibung statt Dokument"-Pfad. Backend setzt chunk_count=0 →
 * /api/trial überspringt den User-RAG-Schritt sauber.
 */
export type TextOnlyCaseType =
  | "rent_reduction"
  | "flight_delay"
  | "product_defect"
  | "consumer_dispute"
  | "employment"
  | "other";

export async function createTextOnlyCase(body: {
  title?: string;
  caseType?: TextOnlyCaseType;
  description?: string;
}): Promise<{
  caseId: string;
  title: string;
  caseType: string;
  mode: "text-only";
}> {
  return request("/api/upload/text-only", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

// --- Conversational Follow-up nach Verdict ---------------------------------

export interface FollowupTurn {
  role: "user" | "assistant";
  content: string;
}

export interface FollowupRequest {
  caseContext: string;
  question: string;
  winProbability?: number;
  estimatedDamagesEur?: number;
  recommendation?: "pursue" | "settle" | "drop";
  recommendationReasoning?: string;
  history?: FollowupTurn[];
}

export interface FollowupResponse {
  answer: string;
  lawyerName: string;
  lawyerTitle: string;
  timestamp: string;
}

export function trialFollowup(body: FollowupRequest): Promise<FollowupResponse> {
  return request<FollowupResponse>("/api/trial/followup", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/**
 * Nach erfolgreichem Upload: Server-seitig PDF parsen (unpdf), chunken,
 * moderieren, in Vectorize indexieren. Macht den User-Fall für /api/trial
 * als RAG-Context verfügbar.
 */
export async function parseCase(caseId: string): Promise<{
  caseId: string;
  indexedChunks: number;
  totalPages: number;
  totalCharacters: number;
}> {
  return request(`/api/upload/${encodeURIComponent(caseId)}/parse`, {
    method: "POST",
    body: "{}",
  });
}

export function runTrial(body: {
  caseId: string;
  caseContext?: string;
  /** Bevorzugter Parameter — wählt den named-persona-Pool (DE/DACH/EU/US). */
  jurisdiction?: Jurisdiction;
  /** Legacy-Alias für jurisdiction. Wird serverseitig auf jurisdiction gemappt. */
  region?: "EU" | "US" | "DE" | "DACH";
  legalCostsEur?: number;
}): Promise<TrialResult> {
  return request<TrialResult>("/api/trial", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/**
 * Sprint I/3-Fix (2026-04-30): Volltext eines Präzedenzfalls nachladen.
 * Endpoint /api/precedents/:vectorId/full lädt:
 *   - bei prec-old:* live von Open Legal Data
 *   - bei prec:*     den kuratierten Snippet aus Vectorize
 * Response wird im Worker per Cache-API 6h gecacht.
 */
export interface PrecedentDetail {
  vectorId: string;
  source: "open_legal_data" | "landmark";
  citation: string;
  fullText: string;
  sourceUrl?: string;
  charCount: number;
}

export async function fetchPrecedentFull(vectorId: string): Promise<PrecedentDetail> {
  return request<PrecedentDetail>(`/api/precedents/${encodeURIComponent(vectorId)}/full`);
}

export function calcDamages(body: Record<string, unknown>): Promise<{
  amountEur: number;
  explanation: string;
}> {
  return request("/api/calc/damages", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

// --- Auto-Drafted Legal Letters --------------------------------------------

export type LetterType =
  | "widerspruch"
  | "mahnung"
  | "mietminderung"
  | "eg261_forderung"
  | "gewaehrleistung";

export interface DraftLetterRequest {
  letterType: LetterType;
  caseContext: string;
  senderName: string;
  senderAddress?: string;
  recipientName: string;
  recipientAddress?: string;
  referenceNumber?: string;
  claimAmountEur?: number;
  claimDeadlineDays?: number;
  language?: "de";
}

export interface DraftedLetter {
  letterType: LetterType;
  subject: string;
  salutation: string;
  bodyParagraphs: string[];
  closing: string;
  signatureName: string;
  legalBasis: string[];
  deadlineDate: string;
  plainText: string;
  markdown: string;
  /** Base64-DOCX — Browser-Download via Blob aus base64. */
  docxBase64: string;
  filename: string;
  disclaimers: string[];
}

export async function draftLetter(body: DraftLetterRequest): Promise<DraftedLetter> {
  return request<DraftedLetter>("/api/draft/letter", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export interface LetterTypeInfo {
  id: LetterType;
  label: string;
  defaultDeadlineDays: number;
}

export async function listLetterTypes(): Promise<{ types: LetterTypeInfo[] }> {
  return request<{ types: LetterTypeInfo[] }>("/api/draft/types");
}

/**
 * Hilfsfunktion: konvertiert die base64-kodierte DOCX-Antwort in einen
 * Blob, den der Browser als Download speichern kann. NICHT auf dem
 * Server zwischenzwischenspeichern — das Base64-Decoding läuft im
 * User-Browser, ohne Roundtrip.
 */
export function docxBase64ToBlob(base64: string): Blob {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], {
    type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  });
}

// --- Steuer-Strukturierungs-Engine + Lawyer-Match ---------------------------

export interface StructuringProfile {
  currentResidence: string;
  targetResidence?: string;
  citizenships: string[];
  netWorthEur: number;
  assetClasses: string[];
  familySituation: string;
  goals: string[];
  timeHorizon: string;
  difficultSituations?: string[];
  additionalContext?: string;
}

export interface StructuringOption {
  id: string;
  name: string;
  jurisdiction: string;
  category: string;
  fitSummary: string;
  taxImpact: string[];
  compliance: string[];
  substance: string[];
  setupCostEur: { min: number; max: number };
  ongoingCostEurPerYear: { min: number; max: number };
  abuseRiskScore: number;
  requiresLocalCounsel: boolean;
  fitScore: number;
  pros: string[];
  cons: string[];
}

export interface StructuringAnalysisResult {
  options: StructuringOption[];
  overallAssessment: string;
  criticalQuestionsForLawyer: string[];
  disclaimers: string[];
  createdAt: string;
}

export interface AdvisorSummary {
  id: string;
  name: string;
  title: string;
  affiliation: string;
  location: string;
  specialties: string[];
  languages: string[];
  hourlyRateEur: { min: number; max: number };
  bio: string;
  status: "fictional" | "verified";
}

export interface StructuringAnalyzeResponse {
  analysis: StructuringAnalysisResult;
  suggestedAdvisors: AdvisorSummary[];
}

export function analyzeStructuring(profile: StructuringProfile): Promise<StructuringAnalyzeResponse> {
  return request<StructuringAnalyzeResponse>("/api/structuring/analyze", {
    method: "POST",
    body: JSON.stringify(profile),
  });
}

export function listAdvisors(): Promise<{ advisors: AdvisorSummary[]; note: string }> {
  return request("/api/structuring/advisors");
}

export interface InquiryRequest {
  advisorId: string;
  profileSummary: string;
  selectedOptionIds?: string[];
  contactName: string;
  contactEmail: string;
  contactPhone?: string;
  consentGdpr: true;
}

export interface InquiryResponse {
  ok: true;
  inquiryId: string;
  advisor: { name: string; affiliation: string; location: string };
  nextSteps: string[];
  legalNote: string;
}

export function submitStructuringInquiry(body: InquiryRequest): Promise<InquiryResponse> {
  return request<InquiryResponse>("/api/structuring/inquire", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function registerAffiliate(body: {
  email: string;
  displayName: string;
  refCode?: string;
}): Promise<{ userId: string; refCode: string; referrerUserId: string | null }> {
  return request("/api/affiliate/register", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function affiliateEarnings(userId: string): Promise<{
  paidEur: number;
  pendingEur: number;
  commissionCount: number;
  byTier: Array<{ tier: number; n: number; total_cents: number }>;
}> {
  return request(`/api/affiliate/${userId}/earnings`);
}
